import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy, faMedal, faAward, faLocationDot,
  faCalendar, faBuilding, faExpand, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import awardData from './awardData';
//import Footer from '../Footer';
import './Awards.css';

/* ─── Animation variants ─────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

var stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.1 } },
};

var scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  show:   { opacity: 1, scale: 1, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

/* ─── Icon picker per index ──────────────────────────────────── */
var ICONS = [faTrophy, faMedal, faAward];

/* ─── Lightbox ───────────────────────────────────────────────── */
var Lightbox = function(props) {
  var award    = props.award;
  var onClose  = props.onClose;

  return (
    <motion.div
      className="award-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={'Award detail: ' + award.title}
    >
      <motion.div
        className="award-lightbox__inner"
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1,    y: 0  }}
        exit={{    opacity: 0, scale: 0.92, y: 24 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={function(e) { e.stopPropagation(); }}
      >
        <button
          className="award-lightbox__close"
          onClick={onClose}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {award.image && (
          <div className="award-lightbox__img-wrap">
            <img src={award.image} alt={award.title} />
          </div>
        )}

        <div className="award-lightbox__body">
          <span className="home-label">Award</span>
          <h2 className="award-lightbox__title">{award.title}</h2>

          <div className="award-lightbox__meta">
            <span><FontAwesomeIcon icon={faBuilding} /> {award.issuer}</span>
            {award.location && <span><FontAwesomeIcon icon={faLocationDot} /> {award.location}</span>}
            <span><FontAwesomeIcon icon={faCalendar} /> {award.date}</span>
          </div>

          <p className="award-lightbox__desc">{award.description}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── Award card ─────────────────────────────────────────────── */
var AwardCard = function(props) {
  var award     = props.award;
  var index     = props.index;
  var onExpand  = props.onExpand;
  var icon      = ICONS[index % ICONS.length];

  if (award.placeholder) {
    return (
      <motion.div className="award-card award-card--placeholder" variants={scaleIn}>
        <div className="award-card__icon-wrap" aria-hidden="true">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="award-card__body">
          <span className="award-card__coming">Coming Soon</span>
          <p className="award-card__placeholder-text">
            More awards will be added here as they are received.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.article
      className="award-card"
      variants={scaleIn}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      aria-label={award.title}
    >
      {/* Image */}
      {award.image && (
        <div className="award-card__img-wrap">
          <img src={award.image} alt={award.title} loading="lazy" />
          <div className="award-card__img-overlay" aria-hidden="true" />
          <button
            className="award-card__expand-btn"
            onClick={function() { onExpand(award); }}
            aria-label={'Expand details for ' + award.title}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>
      )}

      {/* Body */}
      <div className="award-card__body">
        {/* Trophy icon */}
        <div className="award-card__icon-wrap" aria-hidden="true">
          <FontAwesomeIcon icon={icon} />
        </div>

        <h3 className="award-card__title">{award.title}</h3>

        {/* Meta */}
        <div className="award-card__meta">
          <span className="award-card__meta-item">
            <FontAwesomeIcon icon={faBuilding} /> {award.issuer}
          </span>
          {award.location && (
            <span className="award-card__meta-item">
              <FontAwesomeIcon icon={faLocationDot} /> {award.location}
            </span>
          )}
          <span className="award-card__meta-item award-card__meta-item--date">
            <FontAwesomeIcon icon={faCalendar} /> {award.date}
          </span>
        </div>

        <p className="award-card__desc">{award.description}</p>

        <button
          className="award-card__detail-btn"
          onClick={function() { onExpand(award); }}
          aria-label={'View full details for ' + award.title}
        >
          View Details ↗
        </button>
      </div>
    </motion.article>
  );
};

/* ─── Main component ─────────────────────────────────────────── */
var Awards = function() {
  var lightboxState = useState(null);
  var selected      = lightboxState[0];
  var setSelected   = lightboxState[1];

  var realCount = awardData.filter(function(a) { return !a.placeholder; }).length;

  return (
    <motion.div
      className="awards-root"
      initial="hidden"
      animate="show"
      variants={stagger}
    >

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="awards-hero" aria-label="Awards header">
        <div className="awards-hero__glow awards-hero__glow--1" aria-hidden="true" />
        <div className="awards-hero__glow awards-hero__glow--2" aria-hidden="true" />
        <Container>
          <motion.span className="home-label" variants={fadeUp}>Recognition</motion.span>
          <motion.h1 className="awards-hero__title" variants={fadeUp}>
            Awards &amp; <em>Honours.</em>
          </motion.h1>
          <motion.p className="awards-hero__sub" variants={fadeUp}>
            A collection of recognitions received for contributions to software engineering,
            education, and technology.
          </motion.p>
          <motion.div className="awards-hero__stat" variants={fadeUp}>
            <span className="awards-hero__stat-value">{realCount}</span>
            <span className="awards-hero__stat-label">Award{realCount !== 1 ? 's' : ''} Received</span>
          </motion.div>
        </Container>
      </section>

      {/* ── Awards grid ──────────────────────────────────────── */}
      <section className="awards-section" aria-labelledby="awards-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Awards</span>
            <h2 id="awards-heading" className="home-section__title">All Awards</h2>
            <p className="home-section__sub">
              Click any award card to view the full details and image.
            </p>
          </motion.div>

          <motion.div className="awards-grid" variants={stagger}>
            {awardData.map(function(award, i) {
              return (
                <AwardCard
                  key={award.id}
                  award={award}
                  index={i}
                  onExpand={setSelected}
                />
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* ── Lightbox ─────────────────────────────────────────── */}
      <AnimatePresence>
        {selected && (
          <Lightbox
            award={selected}
            onClose={function() { setSelected(null); }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Awards;